import React from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, Svg, Polygon, Line, Path, Rect, G } from '@react-pdf/renderer';
import { Assessment } from '../types';
import { brandColors } from '../config/brandColors';

interface MaturityAnalysis {
  dimensionScores: Array<{
    dimension: string;
    score: number;
    maxScore: number;
    percentage: number;
    answeredCount: number;
    questionCount: number;
  }>;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}

interface PDFReportGeneratorProps {
  assessment: Assessment;
  maturityAnalysis: MaturityAnalysis;
  clientName: string;
  advisorName: string;
  className?: string;
  children?: React.ReactNode;
  canViewScores?: boolean;
}

// PDF Styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: brandColors.primary[600],
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: brandColors.neutral[800],
    marginBottom: 15,
  },
  subtitle: {
    fontSize: 12,
    color: brandColors.neutral[500],
    marginBottom: 2,
  },
  clientSubtitle: {
    fontSize: 12,
    color: brandColors.neutral[500],
    marginBottom: 8,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: brandColors.neutral[800],
    marginBottom: 10,
  },
  // Strengths & Weaknesses
  swContainer: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 10,
  },
  swBox: {
    flex: 1,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
  },
  swHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  swTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 6,
  },
  swItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    padding: 6,
    borderRadius: 4,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
  },
  swText: {
    fontSize: 9,
    color: '#1F2937',
    flex: 1,
    marginRight: 8,
  },
  swScore: {
    fontSize: 9,
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 9,
    color: brandColors.neutral[400],
    borderTopWidth: 1,
    borderTopColor: brandColors.neutral[200],
    paddingTop: 10,
  },
  // Maturity Scale
  scaleContainer: {
    marginBottom: 8,
    marginTop: 5,
  },
  scaleLabelContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  scaleLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    width: '100%',
  },
  // Dimension Bars
  dimRow: {
    marginBottom: 8,
  },
  dimHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  dimName: {
    fontSize: 10,
    fontWeight: 'bold',
    color: brandColors.neutral[700],
  },
  dimScore: {
    fontSize: 10,
    fontWeight: 'bold',
    color: brandColors.neutral[600],
  },
  dimBarContainer: {
    height: 10,
    backgroundColor: '#F3F4F6',
    borderRadius: 5,
    overflow: 'hidden',
    position: 'relative',
  },
  dimBarCompletion: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: '#D1D5DB', // Gray-300
  },
  dimBarScore: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: 5,
  },
});

// Helper for Maturity Progression Graphic
const MaturityProgressionHeader = () => {
  // Colors based on the provided image (blue gradient)
  const colors = [
    '#6366F1', // Initial - Indigo 500
    '#4F46E5', // Managed - Indigo 600
    '#4338CA', // (Re)Defined - Indigo 700
    '#3730A3', // Measured - Indigo 800
    '#312E81', // Optimizing - Indigo 900
  ];
  const labels = ['Initial', 'Managed', '(Re)Defined', 'Measured', 'Optimizing'];

  const width = 535; // Page width approx (A4 is 595pt - 60 margins)
  const height = 25;
  const segmentWidth = width / 5;
  const arrowDepth = 10;

  return (
    <View style={{ marginBottom: 15 }}>
      <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#6B7280', marginBottom: 5, textTransform: 'uppercase' }}>
        Maturity Progression
      </Text>
      <Svg width={width} height={height}>
        {labels.map((label, i) => {
          const x = i * segmentWidth;
          const isFirst = i === 0;

          let points = '';
          const w = segmentWidth;

          if (isFirst) {
            points = `${x},0 ${x + w},0 ${x + w + arrowDepth},${height / 2} ${x + w},${height} ${x},${height}`;
          } else {
            const leftEdge = x;
            const rightEdge = x + w;
            points = `
               ${leftEdge},0 
               ${rightEdge},0 
               ${rightEdge + arrowDepth},${height / 2} 
               ${rightEdge},${height} 
               ${leftEdge},${height} 
               ${leftEdge + arrowDepth},${height / 2}
             `;
          }

          return (
            <G key={i}>
              <Polygon points={points} fill={colors[i]} />
              <Text
                x={x + segmentWidth / 2 + (isFirst ? 0 : arrowDepth / 2)}
                y={height / 2 + 3}
                fontSize={8}
                fontWeight="bold"
                fill="white"
                textAnchor="middle"
              >
                {label}
              </Text>
            </G>
          );
        })}
      </Svg>
    </View>
  );
};

// Helper to get progression label
const getProgressionLabel = (percentage: number): string => {
  if (percentage <= 20) return 'Initial';
  if (percentage <= 40) return 'Managed';
  if (percentage <= 60) return '(Re)Defined';
  if (percentage <= 80) return 'Measured';
  return 'Optimizing';
};

// Helper to get progression color
const getProgressionColor = (percentage: number): string => {
  if (percentage <= 20) return '#6366F1'; // Indigo-500
  if (percentage <= 40) return '#4F46E5'; // Indigo-600
  if (percentage <= 60) return '#4338CA'; // Indigo-700
  if (percentage <= 80) return '#3730A3'; // Indigo-800
  return '#312E81'; // Indigo-900
};

// Spider Chart Component
const PDFSpiderChart = ({ data, size = 300, showValues = true }: { data: any[], size?: number, showValues?: boolean }) => {
  const paddingX = 85; // horizontal padding to prevent label clipping
  const width = size + paddingX * 2;
  const height = size;
  const cx = width / 2;
  const cy = height / 2;
  const radius = (size / 2) - 60; // Web is about 50-60% larger than original size 55
  const numPoints = data.length;

  const getPoint = (value: number, index: number, max: number = 100) => {
    const angle = (Math.PI * 2 * index) / numPoints - Math.PI / 2;
    const r = (radius * value) / max;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    return { x, y };
  };

  const levels = [20, 40, 60, 80, 100];
  const points = data.map((d, i) => getPoint(d.percentage, i)).map(p => `${p.x},${p.y}`).join(' ');

  return (
    <View style={{ width: width, height: height, alignSelf: 'center', marginBottom: 15 }}>
      {/* react-pdf's Svg component */}
      <Svg width={width} height={height}>
        {/* Grid */}
        {levels.map(level => {
          const levelPoints = data.map((_, i) => {
            const p = getPoint(level, i);
            return `${p.x},${p.y}`;
          }).join(' ');
          return (
            <Polygon key={level} points={levelPoints} stroke={brandColors.neutral[200]} strokeWidth={0.5} fill="none" />
          );
        })}

        {/* Axes */}
        {data.map((d, i) => {
          const end = getPoint(100, i);
          return (
            <Line
              key={i}
              x1={cx}
              y1={cy}
              x2={end.x}
              y2={end.y}
              stroke={brandColors.neutral[200]}
              strokeWidth={0.5}
            />
          );
        })}

        {/* Data */}
        <Polygon points={points} fill={brandColors.secondary[600]} fillOpacity={0.2} stroke={brandColors.secondary[600]} strokeWidth={2} />

        {/* Labels */}
        {data.map((d, i) => {
          const p = getPoint(115, i);
          let anchor: 'start' | 'middle' | 'end' = 'middle';
          if (p.x < cx - 10) anchor = 'end';
          else if (p.x > cx + 10) anchor = 'start';

          let yOffset = 0;
          if (p.y < cy - 10) yOffset = -5;
          else if (p.y > cy + 10) yOffset = 5;

          return (
            <G key={i}>
              <Text
                x={p.x}
                y={p.y + yOffset}
                fontSize={8}
                textAnchor={anchor}
                fill={brandColors.neutral[600]}
              >
                {d.dimension}
              </Text>
              {showValues && (
                <Text
                  x={p.x}
                  y={p.y + yOffset + 10}
                  fontSize={8}
                  textAnchor={anchor}
                  fill={brandColors.neutral[400]}
                >
                  {d.percentage.toFixed(0)}%
                </Text>
              )}
            </G>
          );
        })}
      </Svg>
    </View>
  );
};

// PDF Document Component
export const AssessmentReportPDF: React.FC<PDFReportGeneratorProps> = ({
  assessment,
  maturityAnalysis,
  clientName,
  advisorName,
  canViewScores = false
}) => (
  <Document>
    <Page size="A4" style={styles.page}>

      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>CompassAI Evaluation</Text>
        <Text style={styles.clientSubtitle}>Client: {clientName}</Text>
        <Text style={styles.subtitle}>Evaluation ID: #{assessment.id}</Text>
        <Text style={styles.subtitle}>Advisor: {advisorName}</Text>
        <Text style={styles.subtitle}>
          {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Text>
      </View>

      {/* Spider Chart (Top Element) */}
      <PDFSpiderChart data={maturityAnalysis.dimensionScores} size={300} showValues={canViewScores} />

      {/* Maturity Progression & Dimension Analysis */}
      <View style={styles.section}>
        <MaturityProgressionHeader />

        {maturityAnalysis.dimensionScores.map((dimension) => (
          <View key={dimension.dimension} style={styles.dimRow}>
            <View style={styles.dimHeader}>
              <Text style={styles.dimName}>{dimension.dimension}</Text>
              {canViewScores ? (
                <Text style={styles.dimScore}>{dimension.percentage.toFixed(0)}%</Text>
              ) : (
                <Text style={styles.dimScore}>{getProgressionLabel(dimension.percentage)}</Text>
              )}
            </View>
            <View style={styles.dimBarContainer}>
              {/* Faded bar for completion (only if canViewScores is false) */}
              {!canViewScores && (
                <View
                  style={[
                    styles.dimBarCompletion,
                    { width: `${(dimension.answeredCount / dimension.questionCount) * 100}%` }
                  ]}
                />
              )}
              {/* Score bar */}
              <View
                style={[
                  styles.dimBarScore,
                  {
                    width: `${dimension.percentage}%`,
                    backgroundColor: getProgressionColor(dimension.percentage),
                    opacity: canViewScores ? 1 : 0.8,
                  }
                ]}
              />
            </View>
            {!canViewScores && (
              <Text style={{ fontSize: 8, color: '#6B7280', marginTop: 2 }}>
                {dimension.answeredCount} / {dimension.questionCount} Answered
              </Text>
            )}
          </View>
        ))}
      </View>

      {/* Strengths & Areas for Improvement */}
      <View style={styles.swContainer} wrap={false}>
        {/* Strengths */}
        <View style={[styles.swBox, { backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }]}>
          <View style={styles.swHeader}>
            <Svg width={16} height={16} viewBox="0 0 24 24">
              <Path
                d="M12 2C7.58 2 4 5.58 4 10c0 2.62 1.27 4.94 3.25 6.38L9 22h6l1.75-5.62C18.73 14.94 20 12.62 20 10c0-4.42-3.58-8-8-8zm0 14c-1.1 0-2 .9-2 2h4c0-1.1-.9-2-2-2z"
                fill="#22C55E"
              />
            </Svg>
            <Text style={[styles.swTitle, { color: '#166534' }]}>Strengths</Text>
          </View>

          {canViewScores ? (
            // List View
            maturityAnalysis.strengths.map((strength) => {
              const dim = maturityAnalysis.dimensionScores.find(d => d.dimension === strength);
              return (
                <View key={strength} style={[styles.swItem, { borderColor: '#BBF7D0' }]}>
                  <Text style={styles.swText}>{strength}</Text>
                  <Text style={[styles.swScore, { color: '#166534' }]}>{dim ? dim.percentage.toFixed(0) : 0}%</Text>
                </View>
              );
            })
          ) : (
            // Progression Bar View
            <View style={{ marginTop: 4 }}>
              {maturityAnalysis.strengths.map((strength) => {
                const dim = maturityAnalysis.dimensionScores.find(d => d.dimension === strength);
                return (
                  <View key={strength} style={{ marginBottom: 8, padding: 6, backgroundColor: 'white', borderRadius: 4, borderColor: '#BBF7D0', borderWidth: 1 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                      <Text style={{ fontSize: 9, fontWeight: 'medium', color: '#111827' }}>{strength}</Text>
                      <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#6B7280', textTransform: 'uppercase' }}>
                        {getProgressionLabel(dim?.percentage || 0)}
                      </Text>
                    </View>
                    <View style={{ height: 4, width: '100%', backgroundColor: '#F3F4F6', borderRadius: 2, overflow: 'hidden' }}>
                      <View
                        style={{
                          height: '100%',
                          width: `${dim?.percentage || 0}%`,
                          backgroundColor: getProgressionColor(dim?.percentage || 0)
                        }}
                      />
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </View>

        {/* Areas for Improvement */}
        <View style={[styles.swBox, { backgroundColor: '#FFF7ED', borderColor: '#FED7AA' }]}>
          <View style={styles.swHeader}>
            <Svg width={16} height={16} viewBox="0 0 24 24">
              <Path
                d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
                fill="#F97316"
              />
            </Svg>
            <Text style={[styles.swTitle, { color: '#9A3412' }]}>Areas for Improvement</Text>
          </View>

          {canViewScores ? (
            // List View
            maturityAnalysis.weaknesses.map((weakness) => {
              const dim = maturityAnalysis.dimensionScores.find(d => d.dimension === weakness);
              return (
                <View key={weakness} style={[styles.swItem, { borderColor: '#FED7AA' }]}>
                  <Text style={styles.swText}>{weakness}</Text>
                  <Text style={[styles.swScore, { color: '#EA580C' }]}>{dim ? dim.percentage.toFixed(0) : 0}%</Text>
                </View>
              );
            })
          ) : (
            // Progression Bar View
            <View style={{ marginTop: 4 }}>
              {maturityAnalysis.weaknesses.map((weakness) => {
                const dim = maturityAnalysis.dimensionScores.find(d => d.dimension === weakness);
                return (
                  <View key={weakness} style={{ marginBottom: 8, padding: 6, backgroundColor: 'white', borderRadius: 4, borderColor: '#FED7AA', borderWidth: 1 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                      <Text style={{ fontSize: 9, fontWeight: 'medium', color: '#111827' }}>{weakness}</Text>
                      <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#6B7280', textTransform: 'uppercase' }}>
                        {getProgressionLabel(dim?.percentage || 0)}
                      </Text>
                    </View>
                    <View style={{ height: 4, width: '100%', backgroundColor: '#F3F4F6', borderRadius: 2, overflow: 'hidden' }}>
                      <View
                        style={{
                          height: '100%',
                          width: `${dim?.percentage || 0}%`,
                          backgroundColor: getProgressionColor(dim?.percentage || 0)
                        }}
                      />
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text>
          This evaluation was generated by the CAPRI Compass Evaluation System on{' '}
          {new Date().toLocaleDateString()}
        </Text>
      </View>
    </Page>
  </Document>
);

// Main Component
const PDFReportGenerator: React.FC<PDFReportGeneratorProps> = (props) => {
  const [error, setError] = React.useState<string | null>(null);

  // Validate required data
  const isValidData = props.assessment && props.maturityAnalysis && props.clientName && props.advisorName;

  // Filename format: CAPRI CompassAI Evaluation - {client.name}-{assessment.id}-{yymmdd}.pdf
  const d = new Date();
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const dateStr = `${yy}${mm}${dd}`;

  // Sanitize client name but keep spaces and standard characters
  const cleanClientName = props.clientName.replace(/[<>:"/\\|?*]/g, '');
  const fileName = `CAPRI CompassAI Evaluation - ${cleanClientName}-${props.assessment.id}-${dateStr}.pdf`;

  const handlePDFGeneration = () => {
    try {
      setError(null);

      // Validate data before generating PDF
      if (!isValidData) {
        throw new Error('Missing required data for PDF generation');
      }

      if (!props.maturityAnalysis.dimensionScores || props.maturityAnalysis.dimensionScores.length === 0) {
        throw new Error('No dimension scores available for PDF generation');
      }

    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!isValidData) {
    return (
      <div className="inline-flex flex-col items-start gap-1">
        <button
          disabled
          className="inline-flex items-center px-4 py-2 bg-neutral-400 text-white rounded-md cursor-not-allowed"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>PDF Unavailable</span>
        </button>
        <span className="text-sm text-neutral-500">Missing evaluation data</span>
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <PDFDownloadLink
        document={<AssessmentReportPDF {...props} />}
        fileName={fileName}
        className={props.className || "inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"}
        onClick={handlePDFGeneration}
      >
        {({ loading }: { loading: boolean }) => (
          <div className="flex items-center space-x-2">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Generating PDF...</span>
              </>
            ) : (
              props.children || (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF Report</span>
                </>
              )
            )}
          </div>
        )}
      </PDFDownloadLink>

      {error && (
        <div className="text-sm text-error-600">
          Error: {error}
        </div>
      )}
    </div>
  );
};

export default PDFReportGenerator;
